import { Permission } from '../../../types/permission'
import { OrganizationUser } from '../UsersSlice'
import { AdminRole } from '../type/AdminRole'
import { convertRoleToPermissions } from './AdminPermissions'
import { areStringArraysEqual } from '../../../utils/array/areStringArraysEqual'
import { OrganizationAdmin } from '../../../types'

const RX_ADMIN_ROLE_PERMISSIONS = convertRoleToPermissions('rx_admin')
const PATIENT_ADMIN_ROLE_PERMISSIONS = convertRoleToPermissions('patient_admin')

const isSuperAdminPermissionSet = (permissions: Permission[]): boolean => {
  const required = convertRoleToPermissions('super_admin')
  return (
    required.length > 0 &&
    required.every((p) => permissions.includes(p)) &&
    !permissions.includes(Permission.SUPER_ADMIN_MANAGEMENT)
  )
}

export const hasOwnerOrSuperAdminProfileCapability = (
  admin: Partial<OrganizationAdmin> | null | undefined
): boolean => {
  const actor = admin ?? {}
  const isOwner = actor.isOwner ?? false
  const permissions = actor.permissions ?? []
  const adminActive = actor.active ?? false
  return isOwner || (adminActive && isSuperAdminPermissionSet(permissions))
}

export type AdminRoleTagDisplay = {
  label: string
  colorScheme: 'red' | 'orange' | 'blue' | 'cyan'
}

export const targetUserHasSuperAdminRole = (user: OrganizationUser): boolean =>
  !!user.admin?.active && isSuperAdminPermissionSet(user.admin.permissions ?? [])

export function getAdminRoleTagDisplay(
  admin: Partial<OrganizationAdmin>
): AdminRoleTagDisplay | null {
  if (!admin?.active) {
    return null
  }
  if (admin.isOwner) {
    return { label: 'Owner', colorScheme: 'red' }
  }
  const inferred = inferAdminRoleFromPermissions(admin.permissions)

  switch (inferred) {
    case 'super_admin':
      return { label: 'Super Admin', colorScheme: 'orange' }
    case 'rx_admin':
      return { label: 'Rx Admin', colorScheme: 'blue' }
    case 'patient_admin':
      return { label: 'Patient Admin', colorScheme: 'cyan' }
    default:
      return null
  }
}

export const inferAdminRoleFromPermissions = (
  permissions: Permission[] | undefined | null
): AdminRole | null => {
  if (!permissions?.length) {
    return null
  }
  if (isSuperAdminPermissionSet(permissions)) {
    return 'super_admin'
  }
  if (areStringArraysEqual(permissions, RX_ADMIN_ROLE_PERMISSIONS)) {
    return 'rx_admin'
  }
  if (areStringArraysEqual(permissions, PATIENT_ADMIN_ROLE_PERMISSIONS)) {
    return 'patient_admin'
  }
  return null
}
